import {
  formatAmount,
  formatAmountNumber,
  formatExactAmount,
  formatNominalValue,
  formatPrice,
} from "../utils/currency";
import {
  DateFormatOptions,
  DateTimeFormatOptions,
  formatDate,
  formatDateAndTime,
  formatDateTime,
  formatTime,
  formatWeekday,
  formatYear,
  formatYearMonth,
  TimeFormatOptions,
} from "./dateFormat";
import { formatNumber, formatPercent, FormatPercentOptions } from "../utils/numbers";
import { compact, memoize } from "lodash";
import { Language } from "./languages";
import { DateValue } from "../utils/Dates";
import { getCountryName } from "../utils/countries";
import { Amount } from "../common/common";

export type ListFormatOptions = {
  listType?: "conjunction" | "disjunction" | "unit";
  style?: "long" | "short" | "narrow";
  separator?: string;
};

export type LocaleFormat = {
  formatAmount: (currencyOrAmount: string | Amount, input?: number) => string;
  formatAmountNumber: (input: number) => string;
  formatNominalValue: (currency: string, input?: number) => string;
  formatDateTime: (
    input: number | string | Date | undefined,
    options?: DateFormatOptions,
  ) => string;
  formatDate: (input: number | string | Date | undefined, options?: DateFormatOptions) => string;
  formatExactAmount: (amount: Amount) => string;
  formatTime: (input: number | string | Date | undefined, options?: TimeFormatOptions) => string;
  formatDateAndTime: (
    dateInput: DateValue | null | undefined,
    timeInput:
      | DateValue
      | null
      | undefined
      | { startTime: DateValue | null | undefined; endTime: DateValue | null | undefined },
    options?: DateTimeFormatOptions,
  ) => string;
  formatWeekday: (input: number | string | Date | undefined) => string;
  formatNumber: (input?: number, maximumFractionDigits?: number) => string;
  formatPrice: (currency: string, input?: number) => string;
  formatPercent: (input: number | undefined, options?: FormatPercentOptions) => string;
  formatYearMonth: (input: number | string | Date | null | undefined) => string;
  formatYear: (input: number | string | Date | null | undefined) => string;
  formatList: (input: (string | number)[], options?: ListFormatOptions) => string;
  formatCountry: (code: string) => string;
  formatSsn: (ssn: string, options?: { nationality?: string }) => string;
};

const makeListFormat = memoize(
  (lang: Language, options?: ListFormatOptions) => {
    if (options?.separator !== undefined) {
      return { format: (items: Iterable<string>) => Array.from(items).join(options?.separator) };
    }
    return new Intl.ListFormat(lang, { type: options?.listType, style: options?.style });
  },
  (lang, options) =>
    compact([lang, options?.listType, options?.style, options?.separator]).join("-"),
);

export const createLocaleFormat = memoize((lang: Language): LocaleFormat => {
  return {
    formatAmount: (currencyOrAmount, input) => formatAmount(lang, currencyOrAmount, input),
    formatAmountNumber: (input) => formatAmountNumber(input, lang),
    formatNominalValue: (currency, input) => formatNominalValue(lang, currency, input),
    formatDateTime: (input, options) => formatDateTime(lang, input, options),
    formatDate: (input, options) => formatDate(lang, input, options),
    formatExactAmount: (amount: Amount) => formatExactAmount(lang, amount),
    formatTime: (input, options) => formatTime(lang, input, options),
    formatDateAndTime: (dateInput, timeInput, options) =>
      formatDateAndTime(lang, dateInput, timeInput, options),
    formatWeekday: (input) => formatWeekday(lang, input),
    formatNumber: (input, maximumFractionDigits) =>
      formatNumber(lang, input, maximumFractionDigits),
    formatPrice: (currency, input) => formatPrice(lang, currency, input),
    formatPercent: (input, options) => formatPercent(lang, input, options),
    formatYearMonth: (input: number | string | Date | null | undefined) =>
      formatYearMonth(lang, input),
    formatYear: formatYear,
    formatList: (input, options) =>
      makeListFormat(lang, options).format(input.map((x) => String(x))),
    formatCountry: (code) => getCountryName(code.toUpperCase(), lang) ?? "",
    formatSsn: (ssn, options) => {
      switch (options?.nationality?.toLowerCase()) {
        case "no":
          return ssn.substring(0, 6) + " ...";
        default:
          if (ssn.length >= 9) {
            return ssn.substring(0, 6) + " ...";
          }
          return "...";
      }
    },
  };
});
